import type { ColumnFiltersState, SortingState } from '@tanstack/react-table';
import baseApi from 'shared/api/baseApi';

export interface Property {
  id: number;
  createdAt: string;
  updatedAt: string;
  projectId: number;
  subprojectId: number | null;
  unit: string | null;
  projectReferenceId: string | null;
  projectReferenceSKU: string | null;
  propertyType: string | null;
  model: string | null;
  bedrooms: number;
  bathrooms: number;
  interiorSize: number;
  terraceSize: number | null;
  orientation: string | null;
  floor: number | null;
  description: string | null;
  price: number;
  totalPrice: number;
  discountPercentage: number | null;
  rentPrice: number | null;
  rentPriceUnit: string | null;
  priceCurrency: string | null;
  available: boolean | null;
  year: number | null;
}

export const isPropertyAvailable = (property: Property): boolean => property.available !== false;

export interface StockEquivalencesPayload {
  projectId: number;
  equivalencesFile: File;
  stockIdColumn: string;
  skuColumn: string;
  skuPrefix?: string;
  dryRun: boolean;
}

export interface StockEquivalencesResult {
  status: 'ok';
  dryRun: boolean;
  summary: {
    rows: number;
    invalidRows: number;
    stockIds: number;
    stockIdsMatched: number;
    stockIdsNotFound: number;
    propertiesMatched: number;
    propertiesUpdated: number;
    codesAlreadyPresent: number;
  };
  notFound: string[];
  invalidRows: { row: number; reason: string }[];
  tooLong: { propertyId: number; codes: string[] }[];
  changes: { propertyId: number; from: string; to: string }[];
}

export interface PropertiesResponse {
  properties: Property[];
  total: number;
  totalPages: number;
}

export interface PropertyTypologyStats {
  key: string;
  label: string;
  count: number;
  available: number;
  minPrice: number | null;
  currency: string;
  avgInteriorSize: number | null;
  propertyTypeLabel: string;
}

export interface PropertyStats {
  total: number;
  available: number;
  unavailable: number;
  typologies: PropertyTypologyStats[];
  types: string[];
  orientations: string[];
}

interface GetPropertiesBySubprojectArgs {
  subprojectId: number;
  limit?: number;
  offset?: number;
  filters?: ColumnFiltersState;
  sorting?: SortingState;
}

const propertiesApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getPropertiesBySubproject: build.query<PropertiesResponse, GetPropertiesBySubprojectArgs>({
      query: ({ subprojectId, limit = 20, offset = 0, filters = [], sorting = [] }) => {
        const filterModel = JSON.stringify({
          items: [{ field: 'subprojectId', operator: 'equals', value: String(subprojectId) }],
        });
        const params = new URLSearchParams({
          limit: String(limit),
          offset: String(offset),
          filterModel,
        });
        if (filters.length > 0) params.append('filters', JSON.stringify(filters));
        if (sorting.length > 0) params.append('sorting', JSON.stringify(sorting));
        return { url: `/admin/properties?${params.toString()}`, method: 'GET' };
      },
      providesTags: ['Properties'],
    }),
    getPropertyStatsBySubproject: build.query<PropertyStats, { subprojectId: number }>({
      query: ({ subprojectId }) => ({ url: `/admin/properties/stats?subprojectId=${subprojectId}`, method: 'GET' }),
      providesTags: ['Properties'],
    }),
    syncProperties: build.mutation<
      { message: string; status: 'ok' },
      { projectId: number; subprojectId: number; propertiesFile: File; action: 'replace' | 'add' }
    >({
      query: ({ projectId, subprojectId, propertiesFile, action }) => {
        const body = new FormData();
        body.append('projectId', projectId.toString());
        body.append('subprojectId', subprojectId.toString());
        body.append('propertiesFile', propertiesFile);
        return {
          url: action === 'replace' ? '/admin/propertiesBulk/fileCreation' : '/admin/propertiesBulk/fileUpsert',
          method: 'POST',
          body,
          formData: true,
        };
      },
      invalidatesTags: ['Properties'],
    }),
    uploadStockEquivalences: build.mutation<StockEquivalencesResult, StockEquivalencesPayload>({
      query: ({ projectId, equivalencesFile, stockIdColumn, skuColumn, skuPrefix, dryRun }) => {
        const body = new FormData();
        body.append('projectId', projectId.toString());
        body.append('equivalencesFile', equivalencesFile);
        body.append('stockIdColumn', stockIdColumn);
        body.append('skuColumn', skuColumn);
        if (skuPrefix) body.append('skuPrefix', skuPrefix);
        body.append('dryRun', String(dryRun));
        return {
          url: '/admin/propertiesBulk/equivalences',
          method: 'POST',
          body,
          formData: true,
        };
      },
      // A dry run writes nothing, so only a real upload refreshes the property list
      invalidatesTags: (_result, _error, { dryRun }) => (dryRun ? [] : ['Properties']),
    }),
    exportProperties: build.query<Blob, { projectId: number; subprojectId: number }>({
      query: ({ projectId, subprojectId }) => ({
        url: `/admin/propertiesBulk/export?projectId=${projectId}&subprojectId=${subprojectId}`,
        method: 'GET',
        responseHandler: (response: Response): Promise<Blob> => response.blob(),
        cache: 'no-cache',
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPropertiesBySubprojectQuery,
  useGetPropertyStatsBySubprojectQuery,
  useLazyExportPropertiesQuery,
  useSyncPropertiesMutation,
  useUploadStockEquivalencesMutation,
} = propertiesApi;
